import React from 'react'
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import * as Content from '../Content'

const CONTAINER_STYLES = 'flex w-full m-0 box-border'
const PAGINATION_WRAPPER_STYLES = 'flex box-border p-2 m-0 mx-auto my-0'
const PAGE_STYLES = 'p-2 m-0 box-border'
const CURRENT_PAGE_STYLES =
  'inline-flex items-center justify-center h-8 px-3 rounded-sm bg-contentareabackground text-secondary font-semibold ring-1 ring-contentareaborder'

export interface PaginationEntry {
  cursor: string
}

interface IClickParam {
  page: number
  cursor: string
}

export interface PaginationBarProps {
  currentPage: number
  onPageClick: (param: IClickParam) => void
  previousPages?: PaginationEntry[]
  nextPages?: PaginationEntry[]
  totalPages?: number
}

export const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  nextPages,
  previousPages,
  onPageClick,
  totalPages = 0,
}) => {
  const handlePageClick = ({
    page,
    cursor,
  }: IClickParam): React.MouseEventHandler => (e) => {
    e.preventDefault()
    onPageClick({ page, cursor })
  }

  if ((nextPages || []).length + (previousPages || []).length < 1) {
    return <React.Fragment />
  }

  const showFirstPageButton = currentPage > 1
  const showLastPageButton = totalPages > 0 && currentPage < totalPages

  // Add ellipsis indicators when there are pages not shown in the range
  const showStartEllipsis =
    previousPages &&
    previousPages.length > 0 &&
    parseInt(previousPages[0].cursor.replace('page_', '') || '1', 10) > 1
  const showEndEllipsis =
    nextPages &&
    nextPages.length > 0 &&
    currentPage + nextPages.length < totalPages

  return (
    <Content.Text>
      <div className={CONTAINER_STYLES}>
        <ul className={PAGINATION_WRAPPER_STYLES}>
          {/* First page button */}
          {showFirstPageButton && (
            <li className={PAGE_STYLES}>
              <button
                className="text-actionable px-1"
                onClick={handlePageClick({ page: 1, cursor: '' })}
                title="First Page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
            </li>
          )}

          {/* Start ellipsis */}
          {showStartEllipsis && (
            <li className={PAGE_STYLES}>
              <span>...</span>
            </li>
          )}

          {/* Previous pages */}
          {previousPages && previousPages.length > 0
            ? previousPages
                .map((p: any) => p as PaginationEntry)
                .map(({ cursor }, i) => {
                  const page = currentPage - previousPages.length + i
                  return (
                    <li className={PAGE_STYLES} key={cursor}>
                      <button
                        className="text-actionable"
                        onClick={handlePageClick({ page, cursor })}
                      >
                        {page}
                      </button>
                    </li>
                  )
                })
            : null}

          {/* Current page */}
          <li className={PAGE_STYLES}>
            <span className={CURRENT_PAGE_STYLES}>{currentPage}</span>
          </li>

          {/* Next pages */}
          {nextPages && nextPages.length > 0
            ? nextPages
                .map((p: any) => p as PaginationEntry)
                .map(({ cursor }, i) => (
                  <li className={PAGE_STYLES} key={cursor}>
                    <button
                      className="text-actionable"
                      onClick={handlePageClick({
                        cursor,
                        page: currentPage + i + 1,
                      })}
                    >
                      {currentPage + i + 1}
                    </button>
                  </li>
                ))
            : null}

          {/* End ellipsis */}
          {showEndEllipsis && (
            <li className={PAGE_STYLES}>
              <span>...</span>
            </li>
          )}

          {/* Last page button */}
          {showLastPageButton && (
            <li className={PAGE_STYLES}>
              <button
                className="text-actionable px-1"
                onClick={handlePageClick({
                  page: totalPages,
                  cursor: `page_${totalPages}`,
                })}
                title="Last Page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </li>
          )}
        </ul>
      </div>
    </Content.Text>
  )
}
